import React from 'react';
import styled from 'styled-components';
import { motion, useInView } from 'framer-motion';
import { Layers, LayoutDashboard, Waypoints, Zap } from 'lucide-react';
import { Section } from '../styles/CommonElements';
import ShineBorder from './ui/ShineBorder';

const FeatureSection = styled(Section)`
  background: transparent; // Ensure transparency for 3D background
  padding: 8rem 0;
  
  @media (max-width: 768px) {
    padding: 5rem 0;
  }
`;

const SectionLabel = styled.span`
  display: inline-block;
  font-size: 0.875rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 3px;
  color: var(--primary-color);
  margin-bottom: 1rem;
`;

const SectionHeading = styled.h2`
  font-size: 3.5rem;
  font-weight: 900;
  line-height: 1;
  margin-bottom: 2rem;
  color: #111;
  letter-spacing: -0.03em;
  text-transform: uppercase; // "In your face"
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Highlight = styled.span`
  color: var(--primary-color);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  max-width: 1200px;
  margin: 5rem auto 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const CardWrapper = styled(motion.div)`
  perspective: 1000px;
  height: 100%;
`;

// Glassmorphism card, with the lit border supplied by ShineBorder
const Card = styled(ShineBorder)`
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.05); // Soft shadow
  transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275),
    box-shadow 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  
  &:hover {
    box-shadow: 0 20px 60px rgba(255, 107, 0, 0.15); // Orange glow on hover
    transform: translateY(-10px) scale(1.02);
  }
`;

const CardBody = styled.div`
  padding: 3.5rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 2.5rem;
  }
`;

const IconWrapper = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: rgba(255, 107, 0, 0.09);
  border: 1px solid rgba(255, 107, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.75rem;
  transition: background 0.3s ease, border-color 0.3s ease;
  
  svg {
    width: 26px;
    height: 26px;
    stroke-width: 1.75;
    color: var(--primary-color);
    transition: color 0.3s ease;
  }
  
  ${Card}:hover & {
    background: var(--primary-color);
    border-color: var(--primary-color);
  }
  
  ${Card}:hover & svg {
    color: #fff;
  }
`;

const Title = styled.h3`
  font-size: 2rem;
  font-weight: 800;
  color: #111;
  margin: 0 0 1rem;
  letter-spacing: -0.02em;
`;

const Description = styled.p`
  font-size: 1.15rem;
  color: #444;
  line-height: 1.6;
  font-weight: 500;
  margin: 0;
`;

const features = [
  {
    id: 'portals',
    title: 'Business Portals',
    description: 'Centralize everything. Analytics, customers, and reporting in one powerful dashboard.',
    icon: LayoutDashboard,
  },
  {
    id: 'integrations',
    title: 'System Integrations',
    description: 'We make your disjointed tools talk to each other. Eliminate data silos forever.',
    icon: Waypoints,
  },
  {
    id: 'automation',
    title: 'Workflow Automation',
    description: 'Stop doing busy work. Automate repetitive tasks and focus on growth.',
    icon: Zap,
  },
  {
    id: 'enterprise',
    title: 'Enterprise Apps',
    description: 'Scalable, custom-built platforms that run your entire operation.',
    icon: Layers,
  }
];

const Features: React.FC = () => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <FeatureSection id="features">
      <div className="container font--jakarta">
        <SectionLabel>Solutions</SectionLabel>
        <SectionHeading>
          Building <Highlight>software</Highlight><br />that actually works
        </SectionHeading>

        <Grid ref={ref}>
          {features.map((f, i) => (
            <CardWrapper
              key={f.id}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.1, duration: 0.6, ease: "easeOut" }}
            >
              <Card>
                <CardBody>
                  <IconWrapper>
                    <f.icon />
                  </IconWrapper>
                  <Title>{f.title}</Title>
                  <Description>{f.description}</Description>
                </CardBody>
              </Card>
            </CardWrapper>
          ))}
        </Grid>
      </div>
    </FeatureSection>
  );
};

export default Features;
